import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Box } from "../../components/Layout/Box";
import { Content } from "../../components/Layout/Content";
import { PointsChart } from "./PointsChart";
import { PointsDetails } from "./PointsDetails";
import { PointsTable } from "./PointsTable";

import { NFT_PRICES_API, POINTS_API, PRICES_API } from "../../constants/urls";
import { useAccount, useNetwork } from "wagmi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  isNFTCollection,
  nftCollections,
  shellTokens,
  tokenMap,
  tokens,
} from "../../placeholders/tokens";
import { addQuota } from "../../store/quotasSlice";
import { LogoLoader } from "../../components/Loaders";
import { formatUnits } from "@ethersproject/units";
import { BigNumber, Contract } from "ethers";
import { OceanABI } from "../../constants/ABI/OceanABI";
import { CRAB_ADDRESS, OCEAN_ADDRESS } from "../../constants/addresses";
import { addBalance, addNFTBalance } from "../../store/balancesSlice";
import { PoolQuery } from "../../utils/PoolQuery";
import { artifacts } from "../Booty/Items";
import { addPrice } from "../../store/pricesSlice";
import { getNFTs } from "../../utils/nftHelpers";
import { removeLeadingZeros } from "../../utils/ocean/utils";
import { WRAPPED_NFT_MAP } from "../../constants/wrappedNFTs";
import {
  FilterSeasonDropdown,
  SeasonFilter,
} from "./components/FilterSeasonDropdown";
import { HistoricalPointsTable } from "./HistoricalPointsTable";
import { EarnShellPointsBoxes } from "./components/EarnShellPointsBoxes/EarnShellPointsBoxes";
import { Media, breakpoints } from "@/styles";
import { BoxButton } from "@/components/Buttons/Button";
import countdownCrateIcon from "@/assets/booty/countdown-crate-no-bg.png";
import accountantIcon from "@/assets/booty/accountant-head.png";
import { LeaderboardIcon } from "@/components/Icons/LeaderboardIcon";
import { useNavigate } from "react-router";
import { BoostBox } from "./components/BoostBox";
import { CrabABI } from "../../constants/ABI/CrabABI";
import { formatDisplay } from "../../utils/formatDisplay";
import { HistoryIcon } from "@/components/Icons/HistoryIcon";
import {
  updateBoosts,
  updateCurrentPoints,
  updateHistoricalPoints,
} from "@/store/pointsSlice";
import {
  dateFilters,
  seasonFilters,
  CURRENT_SEASON,
  convertToSlug,
} from "../../types/ChartTypes";
import { CONNECTED_CHAIN } from "@/constants/chains";
import { TGECountdown } from "./components/TGECountdown";
import { useWidthBreakpoint } from "@/hooks";
import { useEthersProvider } from '@/utils/wagmi-ethers-adapters/ethers';

export const PointsScreen = () => {
  const navigate = useNavigate();
  const boxes = [
    {
      image: accountantIcon,
      headerText: "Accountant's Trove",
      descriptionText: `Activate boosters to earn more Points`,
      onClickButton: () => navigate("/booty"),
    },
    {
      image: countdownCrateIcon,
      headerText: "The Final Countdown",
      descriptionText: "Get the new Countdown Crate!",
      onClickButton: () => navigate("/lbp/frACCT-21"),
    },
  ];
  const { address: walletAddress, isConnected } = useAccount();
  const provider = useEthersProvider();

  const { chain: activeChain } = useNetwork();
  const validChain = activeChain?.name == CONNECTED_CHAIN;

  const isSmallMobile = useWidthBreakpoint(breakpoints.smallMobile);

  const poolQuery = new PoolQuery(provider);

  const [pointData, setPointData] = useState({
    totalShellPoints: 0,
    forecastedPointsGain: 0,
    compoundedPoints: 0,
    boost: {},
    tokens: {},
  });

  const [isLoading, setIsLoading] = useState(false);
  const [totalValue, setTotalValue] = useState(0);
  const [crabBalance, setCrabBalance] = useState("0");

  const ocean = new Contract(OCEAN_ADDRESS, OceanABI, provider);

  const userQuotas = useAppSelector((state) => state.quotas.quotas);
  const userBoosts =
    useAppSelector((state) => state.points.boosts)[walletAddress!] ?? {};
  const userCurrentPoints = useAppSelector(
    (state) => state.points.currentSeason
  );
  const userHistoricalPoints = useAppSelector(
    (state) => state.points.historicalSeason
  );
  const dispatch = useAppDispatch();

  const [seasonFilter, setSeasonFilter] = useState<SeasonFilter>({
    label: CURRENT_SEASON,
  });
  const [selectedDateFilter, setSelectedDateFilter] = useState("All");

  const getPrices = async () => {
    const priceData: { [id: string]: any } = {};

    const [pricesResponse, nftPricesResponse] = await Promise.all([
      fetch(PRICES_API).then((response) => response.json()),
      fetch(NFT_PRICES_API).then((response) => response.json()),
    ]);

    if (pricesResponse) {
      Object.keys(pricesResponse).forEach((tokenID) => {
        const tokenPrice = pricesResponse[tokenID];
        dispatch(addPrice({ name: tokenID, price: tokenPrice }));
        dispatch(addPrice({ name: "sh" + tokenID, price: tokenPrice }));
        priceData[tokenID] = tokenPrice;
        priceData["sh" + tokenID] = tokenPrice;
      });
    } else {
      console.error("Server not responding properly");
      return {};
    }

    if (nftPricesResponse) {
      Object.keys(nftPricesResponse).forEach((tokenID) => {
        const tokenPrice = nftPricesResponse[tokenID];
        dispatch(addPrice({ name: tokenID, price: tokenPrice }));
        dispatch(addPrice({ name: "sh" + tokenID, price: tokenPrice }));
        priceData[tokenID] = tokenPrice;
        priceData["sh" + tokenID] = tokenPrice;
      });
    }

    const shellTokenPrices = await Promise.all(
      shellTokens.map((shellToken) =>
        poolQuery.getUSDPrice(shellToken, priceData)
      )
    );

    if (shellTokenPrices) {
      shellTokens.forEach((shellToken, i) => {
        const tokenPrice = shellTokenPrices[i];
        dispatch(addPrice({ name: shellToken.symbol, price: tokenPrice }));
        priceData[shellToken.symbol] = tokenPrice;
      });
    }

    return priceData;
  };

  const queryPoints = async () => {
    if (!isConnected || !validChain) {
      const data = await fetch(POINTS_API + "0xNull/now").then((response) =>
        response.json()
      );
      setPointData({
        totalShellPoints: 0,
        forecastedPointsGain: 0,
        compoundedPoints: 0,
        boost: 0,
        tokens: data.tokens,
      });
      setTotalValue(0);
      setCrabBalance("0");
      return;
    } else if (seasonFilter.label !== CURRENT_SEASON) {
      let pointData;
      const slug = convertToSlug(seasonFilter.label);
      if (
        userHistoricalPoints[walletAddress!] &&
        userHistoricalPoints[walletAddress!][slug]
      ) {
        const cachedData = userHistoricalPoints[walletAddress!][slug];
        pointData = JSON.parse(cachedData);
      } else {
        const data = await fetch(POINTS_API + walletAddress + "/" + slug).then(
          (response) => response.json()
        );
        pointData = {
          totalShellPoints: data.totalShellPoints ?? 0,
          forecastedPointsGain: 0,
          compoundedPoints: data.tokens.compoundedPoints?.totalPoints ?? 0,
          boost: 0,
          tokens: data.tokens,
        };
        dispatch(
          updateHistoricalPoints({
            address: walletAddress,
            season: slug,
            value: JSON.stringify(pointData),
          })
        );
      }
      setPointData(pointData);
      setTotalValue(0);
      setCrabBalance("0");
      return;
    }

    const currentTime = Math.floor(Date.now() / 1000);

    if (userCurrentPoints[walletAddress!]) {
      const cachedData = userCurrentPoints[walletAddress!];
      const pointData = JSON.parse(cachedData.pointData);

      const elapsedTimeRatio = (currentTime - cachedData.time) / (60 * 60 * 24);
      pointData.totalShellPoints +=
        pointData.forecastedPointsGain * elapsedTimeRatio;

      const boostData = pointData.boost;

      if (userBoosts["Active Boost"]) {
        boostData.currentBoost = userBoosts["Active Boost"];
      }

      artifacts.forEach((artifact: any, index) => {
        if (artifact.effect?.type == "Booster") {
          if (boostData.activatedArtifacts[index.toString()]) {
            let boost = boostData.activatedArtifacts[index.toString()];
            boostData.activatedArtifacts[index.toString()].timeRemaining =
              Math.max(
                parseFloat(boost.timeRemaining),
                userBoosts[artifact.name] ?? 0
              );
            dispatch(
              updateBoosts({
                address: walletAddress,
                name: boost.name,
                value: parseFloat(boost.timeRemaining),
              })
            );
          } else if (userBoosts[artifact.name]) {
            boostData.activatedArtifacts[index.toString()] = {
              name: artifact.name,
              timeRemaining: userBoosts[artifact.name],
            };
          }
        }
      });

      setPointData(pointData);
      setTotalValue(cachedData.totalValue);
      setCrabBalance(cachedData.crabBalance);
      return;
    }

    const tokenIDs = shellTokens
      .map((shellToken) => shellToken.name)
      .concat(
        tokens.filter((token) => token.wrapped).map((token) => token.symbol)
      )
      .concat(
        nftCollections
          .filter((collection) => collection.wrapped)
          .map((collection) => collection.symbol)
      );

    const fungibleTokenIDs = tokenIDs.filter(
      (tokenID) => !isNFTCollection(tokenMap[tokenID])
    );

    const [data, balanceResponse, oceanNFTResponse, tokenPrices] =
      await Promise.all([
        fetch(POINTS_API + walletAddress + "/now").then((response) =>
          response.json()
        ),
        ocean.balanceOfBatch(
          fungibleTokenIDs.map((_) => walletAddress),
          fungibleTokenIDs.map((tokenID) => tokenMap[tokenID].oceanID)
        ),
        getNFTs(walletAddress, OCEAN_ADDRESS),
        getPrices(),
      ]);

    const tokenData = data.tokens;

    const fungibleBalances: { [id: string]: number } = {};

    balanceResponse.forEach(
      (balance: any, index: number) =>
        (fungibleBalances[fungibleTokenIDs[index]] = balance)
    );

    let priceError = Object.keys(tokenPrices).length == 0;

    const diffs = [];
    let newTotalValue = 0;

    const userOceanNFTs = oceanNFTResponse.filter(
      (token: any) => token.balance == 1
    );

    for (let i = 0; i < tokenIDs.length; i++) {
      const tokenID = tokenIDs[i];
      const token = tokenMap[tokenID];

      if (isNFTCollection(token)) {
        const collectionID = token.symbol;

        const wrappedIDs = WRAPPED_NFT_MAP[collectionID];

        const userWrappedNFTs: any[] = [];
        userOceanNFTs.forEach((userOceanNFT: any) => {
          const oceanID = removeLeadingZeros(
            BigNumber.from(userOceanNFT.tokenId).toHexString()
          );
          if (wrappedIDs[oceanID])
            userWrappedNFTs.push(parseInt(wrappedIDs[oceanID][0]));
        });

        // Update Redux store with balances and quotas

        const chainBalance = userWrappedNFTs.length;

        dispatch(
          addNFTBalance({ collection: collectionID, items: userWrappedNFTs })
        );

        if (userQuotas[collectionID]) {
          tokenData[collectionID].quota = userQuotas[collectionID];
        } else {
          dispatch(addQuota({ tokenID: collectionID, amount: 0 }));
        }

        diffs.push(Math.abs(chainBalance - tokenData[collectionID].balance));

        let tokenValue = 0;

        if (token.is1155) {
          for (let item of userWrappedNFTs) {
            tokenValue +=
              tokenPrices[tokenID].find((e: any) => e.id == item.id.toString())
                .price *
              item.balance *
              100;
          }
        } else {
          tokenValue = tokenPrices[tokenID] * chainBalance;
        }

        // const tokenValue = tokenPrices[tokenID] * chainBalance;
        newTotalValue += tokenValue;

        // Add new fields to tokenData
        tokenData[collectionID].balance = chainBalance;
        tokenData[collectionID].value = priceError ? 0 : tokenValue;
      } else {
        // Update Redux store with balances and quotas

        const chainBalance = formatUnits(fungibleBalances[tokenID]);
        dispatch(addBalance({ address: token.oceanID, amount: chainBalance }));
        if (userQuotas[tokenID]) {
          tokenData[tokenID].quota = userQuotas[tokenID];
        } else {
          dispatch(
            addQuota({ tokenID: tokenID, amount: tokenData[tokenID].quota })
          );
        }
        diffs.push(
          Math.abs(parseFloat(chainBalance) - tokenData[tokenID].balance)
        );
        
        const tokenValue = tokenPrices[tokenID] * parseFloat(chainBalance);
        newTotalValue += tokenValue;

        // Add new fields to tokenData
        tokenData[tokenID].balance = parseFloat(chainBalance);
        tokenData[tokenID].value = priceError ? 0 : tokenValue;
      }
    }

    setTotalValue(newTotalValue);
    const balanceError = diffs.filter((diff) => diff >= 0.001).length > 0;

    const boostData = data.boost;

    if (userBoosts["Active Boost"]) {
      boostData.currentBoost = userBoosts["Active Boost"];
    } else {
      dispatch(
        updateBoosts({
          address: walletAddress,
          name: "Active Boost",
          value: parseFloat(boostData.currentBoost),
        })
      );
    }

    artifacts.forEach((artifact: any, index) => {
      if (artifact.effect?.type == "Booster") {
        if (boostData.activatedArtifacts[index.toString()]) {
          let boost = boostData.activatedArtifacts[index.toString()];
          boostData.activatedArtifacts[index.toString()].timeRemaining =
            Math.max(
              parseFloat(boost.timeRemaining),
              userBoosts[artifact.name] ?? 0
            );
          dispatch(
            updateBoosts({
              address: walletAddress,
              name: boost.name,
              value: parseFloat(boost.timeRemaining),
            })
          );
        } else if (userBoosts[artifact.name]) {
          boostData.activatedArtifacts[index.toString()] = {
            name: artifact.name,
            timeRemaining: userBoosts[artifact.name],
          };
        }
      }
    });

    const pointData = {
      totalShellPoints: data.totalShellPoints ?? 0,
      forecastedPointsGain: balanceError ? -1 : data.next24hGain ?? 0,
      compoundedPoints:
        seasonFilter.label == "Season One"
          ? tokenData.compoundedPoints.totalPoints
          : 0,
      boost: data.boost,
      tokens: tokenData,
    };

    const crabs = new Contract(CRAB_ADDRESS, CrabABI, provider);
    const crabBalance = formatDisplay(
      formatUnits(await crabs.balanceOf(walletAddress)),
      2
    );

    setPointData(pointData);
    setCrabBalance(crabBalance);

    dispatch(
      updateCurrentPoints({
        address: walletAddress,
        value: {
          time: currentTime,
          pointData: JSON.stringify(pointData),
          totalValue: newTotalValue,
          crabBalance: crabBalance,
        },
      })
    );

    return balanceError || priceError;
  };

  const cancellableTimeout = (
    ms: number
  ): { promise: Promise<void>; cancel: () => void } => {
    let timeoutId: NodeJS.Timeout;
    const promise = new Promise<void>((resolve) => {
      timeoutId = setTimeout(resolve, ms);
    });
    return { promise, cancel: () => clearTimeout(timeoutId) };
  };

  const intervalQuery = async (
    numIntervals: number,
    onCancel: { cancelled: boolean; cancel: () => void }
  ): Promise<boolean> => {
    for (let i = 0; i < numIntervals; i++) {
      const error = await queryPoints();
      if (error) {
        const { promise, cancel } = cancellableTimeout(20 * 1000);
        onCancel.cancel = cancel;
        await promise;
        if (onCancel.cancelled) return false;
      } else {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    let _isMounted = true;
    const onCancel = { cancelled: false, cancel: () => {} };
    if (_isMounted) {
      setIsLoading(true);
    }
    queryPoints()
      .then((error) => {
        if (_isMounted) {
          setIsLoading(false);
        }
        if (error) {
          intervalQuery(5, onCancel).then(() => {
            if (!_isMounted || onCancel.cancelled) return;
            setIsLoading(false);
          });
        }
      })
      .catch((_) => {
        if (_isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      _isMounted = false;
      onCancel.cancelled = true;
      onCancel.cancel();
    };
  }, [walletAddress, validChain, seasonFilter.label]);

  return (
    <Content>
      {isLoading ? (
        <LoadingWrapper>
          <LogoLoader />
        </LoadingWrapper>
      ) : (
        <View>
          <TGECountdown isLoading={isLoading} hideSeconds={isSmallMobile} />
          <PointsDetails
            totalShellPoints={pointData.totalShellPoints}
            forecastedPointsGain={pointData.forecastedPointsGain}
            compoundedPoints={pointData.compoundedPoints}
            boost={pointData.boost}
            totalValue={totalValue}
            seasonFilter={seasonFilter}
            crabBalance={crabBalance}
            isOwnWallet={true}
          />
          <ButtonsContainer>
            <BoxButtonStyled
              onClick={() => {
                navigate({
                  pathname: walletAddress
                    ? `/leaderboard/${walletAddress}`
                    : "/leaderboard",
                });
              }}
            >
              <LeaderboardIcon />
              Leaderboard
            </BoxButtonStyled>
            <BoxButtonStyled
              onClick={() => {
                navigate({ pathname: `/history/${walletAddress || "all"}` });
              }}
            >
              <HistoryIcon />
              History
            </BoxButtonStyled>
            {/* <BoxButtonStyled>
              View on Arbiscan
              <Icon src={arbitrumLogo} />
            </BoxButtonStyled> */}
          </ButtonsContainer>
          {pointData.totalShellPoints && isConnected ? (
            <PointsChart
              tokens={pointData.tokens}
              selectedSeasonFilter={seasonFilter.label}
              selectedDateFilter={selectedDateFilter}
            >
              <ChartHeader>
                <SeasonFiltersContainer>
                  <FilterSeasonDropdown
                    onChange={(filter) => setSeasonFilter(filter)}
                    list={seasonFilters}
                    currentFilter={seasonFilter}
                  />
                </SeasonFiltersContainer>
                {seasonFilter.label === CURRENT_SEASON && (
                  <DateFiltersContainer>
                    {dateFilters.map((item, index) => (
                      <DateFilterButton
                        key={index}
                        onClick={() => setSelectedDateFilter(item.label)}
                        active={item.label === selectedDateFilter}
                      >
                        {item.label}
                      </DateFilterButton>
                    ))}
                  </DateFiltersContainer>
                )}
              </ChartHeader>
            </PointsChart>
          ) : (
            <EarnShellPointsBoxes />
          )}
          <BoostBoxes>
            {boxes.map((box) => (
              <BoostBox key={box.headerText} {...box} />
            ))}
          </BoostBoxes>
          {seasonFilter.label == CURRENT_SEASON ? (
            <PointsTable tokens={pointData.tokens} />
          ) : (
            <HistoricalPointsTable tokens={pointData.tokens} />
          )}
        </View>
      )}
    </Content>
  );
};

const View = styled(Box)`
  padding: 20px 20px 40px;

  ${Media.tablet} {
    padding: 16px 8px 16px;
  }
`;

const LoadingWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  vertical-align: center;
  width: 100%;
  padding: 300px 0 300px 0;
`;

const BoostBoxes = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 20px;
  ${Media.tablet} {
    flex-direction: column;
  }
`;

const ButtonsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 8px;
  ${Media.tablet} {
    flex-direction: column;
  }
`;

const BoxButtonStyled = styled(BoxButton)`
  flex-grow: 1;
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
`;

const DateFilterButton = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 57px;
  height: 32px;
  background: #1e2239;
  border: 1px solid #292941;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  cursor: pointer;
  transition: all 0.3s;
  color: ${({ active }) => (active ? "#FFFFFF" : "#7D7D97")};

  &:hover {
    color: #ffffff;
  }

  ${Media.tablet} {
    width: 42px;
    height: 28px;
  }
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  ${Media.tablet} {
    align-items: flex-start;
    flex-direction: row;
    gap: 8px;
    padding: 0px;
  }
`;

const SeasonFiltersContainer = styled.div`
  display: flex;
  align-items: center;
`;

const DateFiltersContainer = styled.div`
  display: flex;
  gap: 6px;
`;
